import type { CSSProperties } from 'react';

import { Spinner } from '@revelio/ui';

import styles from './custom-dash-generation-overlay.module.css';

const SKELETON_TILES: ReadonlyArray<{
  gridColumn: string;
  gridRow: string;
  durationMs: number;
}> = [
  { gridColumn: '1 / span 8', gridRow: '1 / span 1', durationMs: 1600 },
  { gridColumn: '1 / span 4', gridRow: '2 / span 3', durationMs: 1800 },
  { gridColumn: '5 / span 4', gridRow: '2 / span 3', durationMs: 2000 },
  { gridColumn: '1 / span 4', gridRow: '5 / span 3', durationMs: 2200 },
  { gridColumn: '5 / span 4', gridRow: '5 / span 3', durationMs: 2400 },
];

const DEFAULT_CREATE_MESSAGE = 'Building your custom page…';
const DEFAULT_UPDATE_MESSAGE = 'Updating your custom page…';

interface CustomDashGenerationOverlayProps {
  mode: 'create' | 'update';
  /** Status text mirrored from the chat (e.g. "Generating page…"). */
  message: string | null;
  /**
   * Whether the grid currently has charts on it. We only render the
   * full skeleton overlay for empty grids — an update on an existing
   * page shows a compact banner instead so the user can still see the
   * charts being modified.
   */
  hasExistingItems: boolean;
}

export const CustomDashGenerationOverlay = ({
  mode,
  message,
  hasExistingItems,
}: CustomDashGenerationOverlayProps) => {
  const defaultMessage =
    mode === 'update' ? DEFAULT_UPDATE_MESSAGE : DEFAULT_CREATE_MESSAGE;
  const statusText = message?.trim() || defaultMessage;

  if (hasExistingItems) {
    return (
      <div
        className={styles.banner}
        data-testid="custom-dash-generation-status"
        role="status"
        aria-live="polite"
      >
        <Spinner size="sm" className={styles.bannerSpinner} aria-hidden />
        <p className={styles.bannerText}>{statusText}</p>
      </div>
    );
  }

  return (
    <div
      className={styles.overlay}
      data-testid="custom-dash-generation-overlay"
      role="status"
      aria-live="polite"
    >
      <div className={styles.skeletonGrid}>
        {SKELETON_TILES.map((tile) => (
          <div
            key={`${tile.gridColumn}-${tile.gridRow}`}
            className={styles.skeletonTile}
            style={
              {
                gridColumn: tile.gridColumn,
                gridRow: tile.gridRow,
                '--skeleton-duration': `${tile.durationMs}ms`,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
};
